import { Appointment, ClinicConfig, DEFAULT_CONFIG, Attendance } from '../types';
import { getAuthToken, authenticatedFetch } from './authService';
import { normalizeTemplates, DEFAULT_REMINDER_SETTINGS } from '../utils/messageCatalog';

const API_URL = '/api';

const authHeaders = (): Record<string, string> => {
  const token = getAuthToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
};

// --- Config Service ---

export const getClinicConfig = async (): Promise<ClinicConfig> => {
  try {
    const token = getAuthToken();
    const res = await authenticatedFetch(`${API_URL}${token ? '/admin' : ''}/config`, {
      headers: authHeaders(),
    });
    if (!res.ok) throw new Error('Não foi possível carregar a agenda. Tente novamente.');
    const cfg = await res.json();
    return {
      ...DEFAULT_CONFIG,
      ...cfg,
      whatsappConfig: {
        ...DEFAULT_CONFIG.whatsappConfig,
        ...(cfg.whatsappConfig || {}),
      },
      adminNotifications: { phone: '', panelUrl: window.location.origin + '/#/admin', ...(cfg.adminNotifications || {}) },
      messageTemplates: normalizeTemplates(cfg.messageTemplates),
      reminderSettings: { ...DEFAULT_REMINDER_SETTINGS, ...(cfg.reminderSettings || {}) },
    };
  } catch (err) {
    console.error(err);
    throw err;
  }
};

export const saveClinicConfig = async (config: ClinicConfig): Promise<void> => {
  const res = await authenticatedFetch(`${API_URL}/admin/config`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...authHeaders() },
    body: JSON.stringify(config),
  });
  if (!res.ok) throw new Error('Não foi possível salvar as configurações.');
};

// --- WhatsApp Integration Service ---

export const checkWhatsappStatus = async (config: ClinicConfig['whatsappConfig']) => {
  const res = await authenticatedFetch(`${API_URL}/whatsapp/status`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...authHeaders() },
    body: JSON.stringify(config),
  });
  if (!res.ok) throw new Error('Status check failed');
  return await res.json();
};

export const getWhatsappQrCode = async (config: ClinicConfig['whatsappConfig']) => {
  const res = await authenticatedFetch(`${API_URL}/whatsapp/connect`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...authHeaders() },
    body: JSON.stringify(config),
  });
  if (!res.ok) throw new Error('Failed to get QR');
  return await res.json();
};

export const logoutWhatsapp = async (config: ClinicConfig['whatsappConfig']) => {
  await authenticatedFetch(`${API_URL}/whatsapp/logout`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...authHeaders() },
    body: JSON.stringify(config),
  });
};

// --- Appointment Service ---

export const getAppointments = async (): Promise<Appointment[]> => {
  try {
    const res = await authenticatedFetch(`${API_URL}/appointments`, {
      headers: authHeaders(),
    });
    if (!res.ok) throw new Error('Não foi possível carregar os agendamentos.');
    return await res.json();
  } catch (err) {
    throw err;
  }
};

export const getPublicAvailability = async (): Promise<Appointment[]> => {
  try {
    const res = await authenticatedFetch(`${API_URL}/appointments/availability`);
    if (!res.ok) throw new Error('Não foi possível carregar os horários disponíveis.');
    return await res.json();
  } catch (err) {
    throw err;
  }
};

export const searchAppointmentByCode = async (query: string): Promise<Appointment[] | null> => {
  try {
    const res = await authenticatedFetch(`${API_URL}/appointments/search?query=${encodeURIComponent(query)}`);
    if (res.status === 404) return null;
    if (!res.ok) throw new Error('Search failed');
    const data = await res.json();
    return Array.isArray(data) ? data : [data]; // Construct array if single obj returned (backward compat)
  } catch (err) {
    throw err;
  }
};

export const createAppointment = async (appt: Omit<Appointment, 'id' | 'createdAt' | 'status'>): Promise<Appointment> => {
  const res = await authenticatedFetch(`${API_URL}/appointments`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(appt),
  });

  if (!res.ok) {
    const err = await res.json();
    throw new Error(err.message || "Erro ao agendar.");
  }

  return await res.json();
};

export const createAppointmentsBatch = async (appts: Omit<Appointment, 'id' | 'createdAt' | 'status' | 'code'>[]): Promise<{ created: { date: string; time: string; code: string; attendance?: Attendance }[], errors: { date: string; time: string; error: string }[] }> => {
  const res = await authenticatedFetch(`${API_URL}/appointments/batch`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ appointments: appts }),
  });

  if (!res.ok) {
    const err = await res.json();
    // Propagate the whole error object or a specific structure
    const error: any = new Error(err.message || "Erro ao agendar horários.");
    error.details = err.details; // Attach details if present
    throw error;
  }

  const data = await res.json();

  // Backward compatibility layer (in case api returns straight array)
  if (Array.isArray(data)) {
    return { created: data, errors: [] };
  }

  return data;
};

export const updateAppointmentStatus = async (id: string, status: 'confirmed' | 'cancelled', notes?: string): Promise<void> => {
  const res = await authenticatedFetch(`${API_URL}/appointments/${id}/status`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', ...authHeaders() },
    body: JSON.stringify({ status, notes }),
  });

  if (!res.ok) {
    throw new Error("Erro ao atualizar status.");
  }
};

export const cancelAppointment = async (id: string): Promise<void> => {
  await updateAppointmentStatus(id, 'cancelled');
};

export const manageAppointment = async (code: string, phone: string, action: 'cancel' | 'reschedule', target?: { date: string; time: string; attendance: Attendance }): Promise<string> => {
  const res = await authenticatedFetch(`${API_URL}/appointments/manage`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ code, phone, action, target }) });
  const data = await res.json();
  if (!res.ok) throw new Error(data.message || 'Não foi possível atualizar a sessão.');
  return data.message;
};

export const reviewReschedule = async (id: string, action: 'approve' | 'reject'): Promise<string> => {
  const res = await authenticatedFetch(`${API_URL}/admin/appointments/${id}/reschedule`, { method: 'POST', headers: { 'Content-Type': 'application/json', ...authHeaders() }, body: JSON.stringify({ action }) });
  const data = await res.json();
  if (!res.ok) throw new Error(data.message || 'Não foi possível atualizar o pedido.');
  return data.message;
};
